import { MemoizedSelector, MemoizedSelectorWithProps, createSelector } from '@ngrx/store';
import { StateUtils } from '@spartacus/core';
import {
  EntriesState,
  PagesState,
  PaginationState,
  SearchPageResultState,
  SearchState,
} from '../../../../core/features/store/base-feature-state';
import { AssignedShipTo, Pagination } from '../../../../core/model';
import { PyStateUtils } from '../../../../core/state';
import { AvailableShipTosSearchParamsState, AvailableShipTosState, SoldToState, StateWithSoldTo } from '../sold-to-state';
import { getSoldToState } from './feature.selector';

export const getAvailableShipTosState: MemoizedSelector<StateWithSoldTo, AvailableShipTosState> = createSelector(
  getSoldToState,
  (state: SoldToState) => state && state.availableShipTos
);

export const getAvailableShipTosSearchResultEntities: MemoizedSelector<
  StateWithSoldTo,
  EntriesState<AssignedShipTo> | undefined
> = createSelector(getAvailableShipTosState, (state: AvailableShipTosState) => (state && state.entities) || undefined);

export const getAvailableShipTosSearchResultEntries: MemoizedSelectorWithProps<
  StateWithSoldTo,
  any,
  AssignedShipTo[] | undefined
> = createSelector(getAvailableShipTosSearchResultEntities, (entries: EntriesState<AssignedShipTo>, props) => {
  return (props.keys && entries && props.keys.map((i: string) => entries[i])) || undefined;
});

export const getAvailableShipTosSearchParamsSelector: MemoizedSelector<StateWithSoldTo, AvailableShipTosSearchParamsState> =
  createSelector(getAvailableShipTosState, (state: AvailableShipTosState) => (state && state.searchParams) || undefined);

export const getAvailableShipTosSearchSelector: MemoizedSelector<
  StateWithSoldTo,
  SearchState<SearchPageResultState> | undefined
> = createSelector(getAvailableShipTosState, (state: AvailableShipTosState) => (state && state.search) || undefined);

export const getAvailableShipTosSearchPaginationStateSelector: MemoizedSelector<
  StateWithSoldTo,
  PaginationState<SearchPageResultState> | undefined
> = createSelector(
  getAvailableShipTosSearchSelector,
  getAvailableShipTosSearchParamsSelector,
  (state: SearchState<SearchPageResultState>, searchParams: AvailableShipTosSearchParamsState) => {
    return (searchParams && state && state[searchParams.key]) || undefined;
  }
);

export const getAvailableShipTosSearchPaginationSelector: MemoizedSelector<StateWithSoldTo, Pagination | undefined> =
  createSelector(getAvailableShipTosSearchPaginationStateSelector, (state) => (state && state.pagination) || undefined);

export const getAvailableShipTosSearchPagesSelector: MemoizedSelector<
  StateWithSoldTo,
  PagesState<SearchPageResultState> | undefined
> = createSelector(
  getAvailableShipTosSearchParamsSelector,
  getAvailableShipTosSearchPaginationStateSelector,
  (queryParams: AvailableShipTosSearchParamsState, paginationState: PaginationState<SearchPageResultState>) => {
    return (queryParams && paginationState && paginationState.pages) || undefined;
  }
);

// Gets results only from the current pagination page
export const getAvailableShipTosSearchPageResultsSelector: MemoizedSelector<
  StateWithSoldTo,
  StateUtils.LoaderState<SearchPageResultState>
> = createSelector(
  getAvailableShipTosSearchParamsSelector,
  getAvailableShipTosSearchPagesSelector,
  (queryParams: AvailableShipTosSearchParamsState, pagesState: PagesState<SearchPageResultState>) => {
    return (queryParams && pagesState && pagesState[queryParams.page]) || PyStateUtils.initialLoaderState;
  }
);

// Gets array of IDs of all results between first and current pagination pages
export const getAvailableShipTosCombinedSearchPageResultsIdsSelector: MemoizedSelector<
  StateWithSoldTo,
  Array<string>
> = createSelector(
  getAvailableShipTosSearchParamsSelector,
  getAvailableShipTosSearchPagesSelector,
  (queryParams: AvailableShipTosSearchParamsState, pagesState: PagesState<SearchPageResultState>) => {
    const combinedResults: Array<string> = [];
    if (queryParams && pagesState) {
      [...Array(queryParams.page + 1).keys()].forEach((pageNumber) => {
        if (pagesState[pageNumber]) {
          combinedResults.push(...StateUtils.loaderValueSelector(pagesState[pageNumber]).results);
        }
      });
    }
    return combinedResults;
  }
);
